// AI error toast helper.
//
// FastAPI's HTTPException can carry either a plain `detail: string` or a
// structured `detail: object`. v3.8.3 introduced structured errors for
// the AI feature routes (RTM AI Map, Capability Matrix AI Detect,
// Scorecard AI Prefill) so the user sees the EXACT missing field
// instead of "AI returned invalid format". This helper normalizes
// either shape into a single toast message.
//
// Structured shape (from app/utils/llm_schemas.py):
//   {
//     code: "AI_RESPONSE_INVALID" | "AI_RESPONSE_UNPARSEABLE" | "BYOM_NOT_CONFIGURED",
//     feature: "RTM AI Map",
//     field: "entries",
//     message: "RTM AI Map: AI response is missing required field 'entries' (expected list). Please retry.",
//     raw_head: "..."  // diagnostic only, not surfaced
//   }
//
// Falls back gracefully when the backend returns a string detail (older
// routes) or nothing at all.
export function describeApiError(err, fallback = 'Request failed') {
  if (!err) return fallback;

  // String detail — older routes
  if (typeof err === 'string') return err;

  const detail = err.detail !== undefined ? err.detail : err;

  // String detail — most common path
  if (typeof detail === 'string') return detail;

  // v3.18.31 — Pydantic validation errors arrive as an array of
  // {loc, msg, type} objects. Without this branch the toast showed
  // "[object Object]". Render the array as a readable joined string.
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') {
          const loc = Array.isArray(d.loc) ? d.loc.filter((s) => s !== 'body').join('.') : '';
          return loc ? `${loc}: ${d.msg || d.type || ''}` : (d.message || d.msg || d.type || JSON.stringify(d));
        }
        return String(d);
      })
      .join(' · ');
  }

  // Structured detail with a `.message` — v3.8.3+ AI errors
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    // Last resort: try to pull a useful field
    if (typeof detail.code === 'string') return detail.code;
    // Final fallback: stringify so we never show "[object Object]"
    try { return JSON.stringify(detail); } catch { /* ignore */ }
  }

  return fallback;
}

/**
 * Read a fetch Response that came back !ok and return a readable string.
 * v3.18.31 — replaces ad-hoc `(await res.json()).detail || HTTP ${status}`
 * patterns that produced "[object Object]" toasts when FastAPI returned a
 * list/object detail.
 */
export async function readFetchError(res, fallback = 'Request failed') {
  try {
    const body = await res.json();
    return describeApiError(body, fallback);
  } catch {
    return `HTTP ${res.status}${res.statusText ? ` · ${res.statusText}` : ''}`;
  }
}
